import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";

/** Shared dialogs retain their existing cache contracts. Bridge successful
 * writes into this page using the request's BU, never a newly selected BU. */
export function useWorkspaceRefresh(organizationBusinessUnitId: string) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (event.type !== "updated" || event.action.type !== "success") {
          return;
        }
        const input = event.mutation.state.variables;
        if (
          !input ||
          typeof input !== "object" ||
          !("organizationBusinessUnitId" in input) ||
          input.organizationBusinessUnitId !== organizationBusinessUnitId
        ) {
          return;
        }
        const key = event.mutation.options.mutationKey?.[0];
        if (
          !Array.isArray(key) ||
          ![
            "credentials",
            "psv",
            "organizationBusinessUnitAffiliation",
            "providerWorkspace",
            "credentialFiles",
            "providerPool",
          ].includes(String(key[0]))
        ) {
          return;
        }
        if (String(key[1]).startsWith("get")) {
          return;
        }
        void queryClient.invalidateQueries({
          queryKey: trpc.providerWorkspace.list.queryKey({
            organizationBusinessUnitId,
          }),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.providerWorkspace.getProvider.queryKey({
            organizationBusinessUnitId,
          }),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.providerWorkspace.listInvitations.queryKey({
            organizationBusinessUnitId,
          }),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.providerWorkspace.rosterDetails.queryKey({
            organizationBusinessUnitId,
          }),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.providerWorkspace.poolOptions.queryKey({
            organizationBusinessUnitId,
          }),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.providerWorkspace.poolDetails.queryKey({
            organizationBusinessUnitId,
          }),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.providerWorkspace.legacyDetail.queryKey({
            organizationBusinessUnitId,
          }),
        });
      }),
    [organizationBusinessUnitId, queryClient, trpc],
  );
}
